import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Scissors, RefreshCw } from 'lucide-react';
import { apiService } from '@/services/api';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import SalonBookingWizard, { useSalonFont, WizardService, WizardStaff, WizardSubmitPayload } from '@/components/SalonBooking/SalonBookingWizard';

/**
 * PublicBooking — the shareable salon booking widget at `/book/:token`. A guest self-books a service +
 * professional + time with no login; the request lands in the owner's diary as `requested`. This is the
 * link a salon posts on Instagram / Google / WhatsApp. Thin token wrapper around SalonBookingWizard.
 */
type Phase = 'loading' | 'error' | 'ready';

const PublicBooking: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  useSalonFont();

  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState('');
  const [branchName, setBranchName] = useState('');
  const [currency, setCurrency] = useState<string | null>(null);
  const [services, setServices] = useState<WizardService[]>([]);
  const [staff, setStaff] = useState<WizardStaff[]>([]);

  useEffect(() => {
    if (!token) { setPhase('error'); setMessage('This booking link is not recognised.'); return; }
    let alive = true;
    (async () => {
      setPhase('loading');
      try {
        const res = await apiService.getPublicBooking(token);
        if (!alive) return;
        const d = res?.data || {};
        setBranchName(d.branchName || 'Salon');
        setCurrency(d.currency || null);
        setServices((d.services || []) as WizardService[]);
        setStaff((d.staff || []) as WizardStaff[]);
        setPhase('ready');
      } catch (e) {
        if (!alive) return;
        setPhase('error'); setMessage(e instanceof Error ? e.message : 'Something went wrong.');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const loadSlots = async (staffId: string, date: string, serviceId?: string) => {
    const r = await apiService.getPublicBookingSlots(token!, staffId, date, serviceId);
    return (r?.data?.slots || []) as string[];
  };
  const submit = async (p: WizardSubmitPayload) => {
    const r = await apiService.createPublicBooking(token!, { serviceId: p.serviceId, staffId: p.staffId, date: p.date, time: p.time, name: p.name, phone: p.phone, notes: p.notes });
    const d = r?.data || {};
    return { time: d.time, date: d.date, serviceName: d.serviceName ?? null, staffName: d.staffName ?? null };
  };

  const joinWaitlist = async (p: { serviceId?: string; staffId?: string; date: string; timePref: string; name: string; phone?: string }) => {
    await apiService.joinPublicWaitlist(token!, p);
  };

  return (
    <div style={styles.page}>
      <ErrorBoundary>
        {phase === 'loading' ? (
          <div style={styles.splash}>
            <div style={styles.splashMark}><Scissors size={30} color="#fff" /></div>
            <div style={styles.splashName}>{branchName || 'Loading'}</div>
            <div style={styles.splashSub}>Getting things ready…</div>
          </div>
        ) : phase === 'error' ? (
          <div style={styles.shell}><div style={styles.center}>
            <div style={styles.stateIcon}><RefreshCw size={26} color="#b91c1c" /></div>
            <div style={styles.centerTitle}>Can’t open this page</div>
            <div style={styles.muted}>{message}</div>
          </div></div>
        ) : (
          <div style={styles.shell}>
            <SalonBookingWizard branchName={branchName} currency={currency} services={services} staff={staff} loadSlots={loadSlots} submit={submit} joinWaitlist={joinWaitlist} />
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
};

const SERIF = "'Fraunces','Iowan Old Style','Palatino Linotype','Palatino','Georgia',serif";
const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100dvh', background: '#efe7dc', display: 'flex', justifyContent: 'center' },
  shell: { width: 'min(500px, 100%)', minHeight: '100dvh', background: '#f6f1ea', display: 'flex', flexDirection: 'column', boxShadow: '0 0 60px rgba(33,27,24,0.10)', padding: '0 18px calc(0px + env(safe-area-inset-bottom))', boxSizing: 'border-box' },
  splash: { width: 'min(500px,100%)', minHeight: '100dvh', background: '#f6f1ea', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 },
  splashMark: { width: 74, height: 74, borderRadius: 22, background: 'linear-gradient(135deg,#a56a3c,#c79a5b)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 34px rgba(165,106,60,0.4)' },
  splashName: { fontFamily: SERIF, fontSize: '1.5rem', fontWeight: 600, marginTop: 4, color: '#211b18' },
  splashSub: { color: '#8a7d6f', fontSize: '0.9rem' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '60px 24px' },
  stateIcon: { width: 60, height: 60, borderRadius: '50%', background: 'rgba(185,28,28,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  centerTitle: { fontFamily: SERIF, fontSize: '1.3rem', fontWeight: 600, color: '#211b18' },
  muted: { color: '#8a7d6f', fontSize: '0.95rem', lineHeight: 1.5 },
};

export default PublicBooking;
